<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useFavoriteGames } from '~/composables/useFavoriteGames';
import { useAuthStore } from '~/stores/auth';

interface Props {
  gameId: number;
  size?: 'small' | 'medium' | 'large';
  showFeedback?: boolean;
  hideWhenNotFavorited?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  showFeedback: true,
  hideWhenNotFavorited: false,
});

const authStore = useAuthStore();
const { userId } = useUserData();
const { toggleFavorite, isFavorite, onFavoriteGamesUpdate } =
  useFavoriteGames();

const isFavorited = ref(false);
const isLoading = ref(false);
const hasError = ref(false);
const showSuccessMessage = ref(false);
const successMessage = ref('');

const isAuthenticated = computed(() => authStore.isAuthenticated);

const updateFavoriteStatus = () => {
  if (!isAuthenticated.value) {
    isFavorited.value = false;

    return;
  }

  isFavorited.value = isFavorite(props.gameId);
};

const messageType = ref<'added' | 'removed'>('added');

const handleToggle = async () => {
  if (!isAuthenticated.value || isLoading.value) return;
  if (!userId.value) return;

  hasError.value = false;
  showSuccessMessage.value = false;
  isLoading.value = true;

  // Atualiza UI otimisticamente
  const previousState = isFavorited.value;
  const wasAdding = !previousState; // Se estava desmarcado, está adicionando

  isFavorited.value = !isFavorited.value;

  try {
    const newStatus = await toggleFavorite(String(userId.value), props.gameId);

    // Se a API retornou um estado diferente do que esperávamos, corrige
    if (newStatus !== isFavorited.value) {
      isFavorited.value = newStatus;
    }

    // Mostra mensagem de sucesso ao adicionar ou remover (apenas se showFeedback estiver ativo)
    if (props.showFeedback) {
      if (wasAdding && newStatus) {
        // Adicionou aos favoritos
        successMessage.value = 'Adicionado aos Favoritos';
        messageType.value = 'added';
        showSuccessMessage.value = true;

        setTimeout(() => {
          showSuccessMessage.value = false;
        }, 2000);
      } else if (!wasAdding && !newStatus) {
        // Removeu dos favoritos
        successMessage.value = 'Removido dos Favoritos';
        messageType.value = 'removed';
        showSuccessMessage.value = true;

        setTimeout(() => {
          showSuccessMessage.value = false;
        }, 2000);
      }
    }
  } catch (error) {
    console.error('Erro ao alternar favorito:', error);

    // Reverte em caso de erro
    isFavorited.value = previousState;
    hasError.value = true;

    // Remove erro após 3 segundos
    setTimeout(() => {
      hasError.value = false;
    }, 3000);
  } finally {
    isLoading.value = false;
  }
};

let unsubscribe: (() => void) | null = null;

onMounted(() => {
  updateFavoriteStatus();

  // Registra listener para atualizações
  unsubscribe = onFavoriteGamesUpdate(() => {
    updateFavoriteStatus();
  });
});

watch(isAuthenticated, () => {
  updateFavoriteStatus();
});

watch(
  () => props.gameId,
  () => {
    updateFavoriteStatus();
  },
);

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>

<template>
  <div
    v-if="!hideWhenNotFavorited || isFavorited"
    class="favorite-button-wrapper"
  >
    <button
      :aria-label="
        isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'
      "
      :class="[
        'favorite-button',
        `size-${size}`,
        {
          'is-favorited': isFavorited,
          'is-loading': isLoading,
          'has-error': hasError,
        },
      ]"
      :disabled="!isAuthenticated || isLoading"
      @click="handleToggle"
    >
      <svg
        class="star-icon"
        :fill="isFavorited ? 'currentColor' : 'none'"
        :stroke="isFavorited ? 'currentColor' : '#9ca3af'"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polygon
          points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        />
      </svg>
    </button>

    <Transition
      v-if="showFeedback"
      name="fade-slide"
    >
      <div
        v-if="showSuccessMessage"
        :class="['success-message', messageType]"
      >
        {{ successMessage }}
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.favorite-button-wrapper {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.star-icon {
  transition: all 0.2s ease;
}

.success-message {
  position: absolute;
  top: 100%;
  margin-top: 4px;
  padding: 4px 8px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &.added {
    background: rgba(34, 197, 94, 0.9);
  }

  &.removed {
    background: rgba(249, 115, 22, 0.9);
  }

  @media (max-width: 768px) {
    transform: translateX(-60px);
  }
}

.favorite-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #fbbf24;

  &.size-small {
    width: 28px;
    height: 28px;

    .star-icon {
      width: 16px;
      height: 16px;
    }
  }

  &.size-medium {
    width: 40px;
    height: 40px;

    .star-icon {
      width: 24px;
      height: 24px;
    }
  }

  &.size-large {
    width: 48px;
    height: 48px;

    .star-icon {
      width: 28px;
      height: 28px;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.7);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &.is-loading {
    .star-icon {
      animation: pulse 1s ease-in-out infinite;
    }
  }

  &.has-error {
    color: #ef4444;
    animation: shake 0.5s ease;
  }

  &.is-favorited:not(.is-loading) {
    .star-icon {
      animation: favorite-pop 0.3s ease;
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.85);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

@keyframes favorite-pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
